import React, { useState, useRef } from 'react';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Snackbar from '@material-ui/core/Snackbar';
import Alert from '@material-ui/lab/Alert';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import SendIcon from '@material-ui/icons/Send';
import emailjs from 'emailjs-com';
import { useTranslation } from 'react-i18next';

const background = `/assets/img/background.jpg`;
const useStyles = makeStyles((theme) =>
  createStyles({
    container: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      background: `
        radial-gradient(circle at 20% 50%, rgba(0, 245, 255, 0.15) 0%, transparent 50%),
        radial-gradient(circle at 80% 20%, rgba(199, 125, 255, 0.15) 0%, transparent 50%),
        radial-gradient(circle at 40% 80%, rgba(57, 255, 20, 0.1) 0%, transparent 50%),
        linear-gradient(to bottom, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.8) 100%), 
        url(${background})
      `,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      backgroundAttachment: 'fixed',
      padding: theme.spacing(2),
      position: 'relative',
      overflow: 'hidden',
    },
    topShadow: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '30px',
      background: 'linear-gradient(to bottom, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0) 100%)',
      zIndex: 2,
    },
    bottomShadow: {
      position: 'absolute',
      bottom: 0,
      left: 0,
      width: '100%',
      height: '30px',
      background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0) 100%)',
      zIndex: 2,
    },
    formContainer: {
      width: '90%',
      maxWidth: '600px',
      margin: '0 auto',
      padding: theme.spacing(4),
      boxShadow: '0 25px 50px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1)',
      borderRadius: '20px',
      background: 'rgba(10, 15, 30, 0.85)',
      backdropFilter: 'blur(20px)',
      border: '1px solid rgba(0, 245, 255, 0.2)',
      position: 'relative',
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '2px',
        background: 'linear-gradient(90deg, transparent, #00f5ff, #c77dff, transparent)',
        animation: 'shimmer 3s ease-in-out infinite',
      },
    },
    titleHeader: {
      textAlign: 'center',
      fontFamily: '"JetBrains Mono", monospace',
      fontWeight: 700,
      fontSize: '2.8rem',
      marginBottom: theme.spacing(4),
      background: 'linear-gradient(135deg, #00f5ff, #c77dff, #39ff14)',
      backgroundSize: '300% 300%',
      animation: 'gradientShift 4s ease infinite',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
      textShadow: 'none',
      position: 'relative',
      '&::after': {
        content: '"</>"',
        position: 'absolute',
        top: '-10px',
        right: '-20px',
        fontSize: '1.5rem',
        color: '#00f5ff',
        opacity: 0.7,
      },
      '&::before': {
        content: '"<"',
        position: 'absolute',
        top: '-10px',
        left: '-20px',
        fontSize: '1.5rem',
        color: '#c77dff',
        opacity: 0.7,
      },
    },
    button: {
      width: '100%',
      padding: '16px 24px',
      background: 'linear-gradient(135deg, #3f51b5, #5c6bc0, #7986cb)',
      backgroundSize: '200% 200%',
      color: '#fff',
      fontWeight: 'bold',
      fontFamily: '"Inter", sans-serif',
      border: 'none',
      borderRadius: '12px',
      fontSize: '1.1rem',
      cursor: 'pointer',
      transition: 'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
      position: 'relative',
      overflow: 'hidden',
      textTransform: 'uppercase',
      letterSpacing: '1px',
      boxShadow: '0 8px 25px rgba(63, 81, 181, 0.3)',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: '-100%',
        width: '100%',
        height: '100%',
        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
        transition: 'left 0.5s ease',
      },
      '&:hover': {
        backgroundPosition: '100% 0',
        transform: 'translateY(-3px)',
        boxShadow: '0 12px 35px rgba(63, 81, 181, 0.4)',
        '&::before': {
          left: '100%',
        },
      },
      '&:disabled': {
        background: '#666',
        cursor: 'not-allowed',
        transform: 'none',
        boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
        '&::before': {
          display: 'none',
        },
      },
      marginTop: theme.spacing(3),
    },
    buttonContent: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
    },
    loadingButton: {
      background: 'linear-gradient(135deg, #666, #888) !important',
      cursor: 'not-allowed !important',
      animation: 'pulse 2s ease-in-out infinite !important',
    },
    successButton: {
      background: 'linear-gradient(135deg, #4caf50, #66bb6a, #81c784) !important',
      animation: 'successPulse 1s ease-out !important',
      '&:hover': {
        background: 'linear-gradient(135deg, #45a049, #5cb85c, #76c776) !important',
      },
    },
    error: {
      color: 'red',
      fontSize: '0.875rem',
      marginTop: theme.spacing(1),
    },
    textField: {
      marginBottom: theme.spacing(3),
      '& .MuiOutlinedInput-root': {
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        borderRadius: '12px',
        transition: 'all 0.3s ease',
        backdropFilter: 'blur(10px)',
        '& fieldset': {
          borderColor: 'rgba(0, 245, 255, 0.3)',
          borderWidth: '2px',
        },
        '&:hover fieldset': {
          borderColor: 'rgba(0, 245, 255, 0.6)',
          boxShadow: '0 0 15px rgba(0, 245, 255, 0.2)',
        },
        '&.Mui-focused fieldset': {
          borderColor: '#00f5ff',
          boxShadow: '0 0 20px rgba(0, 245, 255, 0.3)',
        },
        '&.Mui-error fieldset': {
          borderColor: '#ff6b6b',
          boxShadow: '0 0 15px rgba(255, 107, 107, 0.3)',
        },
        '& input, & textarea': {
          color: '#ffffff',
          fontSize: '1rem',
          fontFamily: '"Inter", sans-serif',
        },
      },
      '& .MuiInputLabel-outlined': {
        color: 'rgba(255, 255, 255, 0.7)',
        fontSize: '1rem',
        fontWeight: 500,
        fontFamily: '"Inter", sans-serif',
        '&.Mui-focused': {
          color: '#00f5ff',
        },
        '&.Mui-error': {
          color: '#ff6b6b',
        },
      },
      '& .MuiFormHelperText-root': {
        color: 'rgba(255, 255, 255, 0.6)',
        fontSize: '0.875rem',
        marginTop: '8px',
        '&.Mui-error': {
          color: '#ff6b6b',
        },
      },
    },
    // Animaciones personalizadas
    '@keyframes gradientShift': {
      '0%': { backgroundPosition: '0% 50%' },
      '50%': { backgroundPosition: '100% 50%' },
      '100%': { backgroundPosition: '0% 50%' },
    },
    '@keyframes shimmer': {
      '0%': { transform: 'translateX(-100%)' },
      '100%': { transform: 'translateX(100%)' },
    },
    '@keyframes pulse': {
      '0%': { transform: 'scale(1)' },
      '50%': { transform: 'scale(1.02)' },
      '100%': { transform: 'scale(1)' },
    },
    '@keyframes successPulse': {
      '0%': { transform: 'scale(1)' },
      '50%': { transform: 'scale(1.05)' },
      '100%': { transform: 'scale(1)' },
    },
    // Estilos adicionales para mejor UX
    formWrapper: {
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: '-50%',
        left: '-50%',
        width: '200%',
        height: '200%',
        background: 'conic-gradient(from 0deg, transparent, rgba(0, 245, 255, 0.1), transparent)',
        animation: 'rotate 20s linear infinite',
        zIndex: -1,
        borderRadius: '50%',
      },
    },
    '@keyframes rotate': {
      '0%': { transform: 'rotate(0deg)' },
      '100%': { transform: 'rotate(360deg)' },
    },
  })
);

const Contact = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const formRef = useRef();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobilePhone: '',
    message: '',
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('idle'); // 'idle', 'success', 'error'
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [lastSubmitTime, setLastSubmitTime] = useState(0);

  const validate = () => {
    const newErrors = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = t('contact.validation.nameRequired');
    } else if (!formData.name.match(/^[a-zA-Z\s]{2,50}$/)) {
      newErrors.name = t('contact.validation.nameInvalid');
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = t('contact.validation.emailRequired');
    } else if (!formData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      newErrors.email = t('contact.validation.emailInvalid');
    }

    // Phone validation
    if (!formData.mobilePhone.trim()) {
      newErrors.mobilePhone = t('contact.validation.phoneRequired');
    } else if (!formData.mobilePhone.match(/^\+?[\d\s\-()]{7,15}$/)) {
      newErrors.mobilePhone = t('contact.validation.phoneInvalid');
    }

    // Message validation
    if (!formData.message.trim()) {
      newErrors.message = t('contact.validation.messageRequired');
    } else if (formData.message.length < 10) {
      newErrors.message = t('contact.validation.messageMin');
    } else if (formData.message.length > 500) {
      newErrors.message = t('contact.validation.messageMax');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
    
    // Reset submit status when user modifies form
    if (submitStatus !== 'idle') {
      setSubmitStatus('idle');
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Rate limiting: prevent submissions within 30 seconds
    const now = Date.now();
    if (now - lastSubmitTime < 30000) {
      showSnackbar(t('contact.snackbar.rateLimit'), 'warning');
      return;
    }

    if (!validate()) {
      showSnackbar(t('contact.snackbar.fixErrors'), 'error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await emailjs.send(
        'service_xr7sa0b', // Service ID
        'template_qf58l6m', // Template ID
        {
          ...formData,
          to_name: 'CIAP', // Add recipient name
          from_name: formData.name,
          reply_to: formData.email,
          timestamp: new Date().toLocaleString(),
        },
        'WLQNOeRhg5mMTc2CS' // User ID
      );

      console.log('Email sent successfully!', response.status, response.text);
      
      setSubmitStatus('success');
      setLastSubmitTime(now);
      showSnackbar(t('contact.snackbar.sentSuccess'), 'success');
      
      // Reset the form after successful submission
      setTimeout(() => {
        setFormData({
          name: '',
          email: '',
          mobilePhone: '',
          message: '',
        });
        setErrors({});
        setSubmitStatus('idle');
      }, 3000);

    } catch (error) {
      console.error('Failed to send email:', error);
      setSubmitStatus('error');
      
      let errorMessage = t('contact.snackbar.sendFailed');
      if (error.status === 400) {
        errorMessage = t('contact.snackbar.invalidData');
      } else if (error.status === 403) {
        errorMessage = t('contact.snackbar.serviceUnavailable');
      } else if (error.status >= 500) {
        errorMessage = t('contact.snackbar.serverError');
      }
      
      showSnackbar(errorMessage, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={classes.container}>
      <div className={classes.topShadow}></div>
      <div className={`${classes.formContainer} ${classes.formWrapper}`}>
        <h2 className={classes.titleHeader}>{t('contact.title')}</h2>
        <form ref={formRef} onSubmit={handleSubmit} noValidate>
          <TextField
            id="name"
            name="name"
            label={t('contact.fields.fullName')}
            variant="outlined"
            fullWidth
            className={classes.textField}
            InputLabelProps={{
              shrink: true,
            }}
            value={formData.name}
            onChange={handleChange}
            error={!!errors.name}
            helperText={errors.name}
            disabled={isSubmitting}
            required
            autoComplete="name"
            placeholder={t('contact.fields.fullNamePlaceholder')}
          />
          <TextField
            id="email"
            name="email"
            label={t('contact.fields.email')}
            type="email"
            variant="outlined"
            fullWidth
            className={classes.textField}
            InputLabelProps={{
              shrink: true,
            }}
            value={formData.email}
            onChange={handleChange}
            error={!!errors.email}
            helperText={errors.email}
            disabled={isSubmitting}
            required
            autoComplete="email"
            placeholder={t('contact.fields.emailPlaceholder')}
          />
          <TextField
            id="mobilePhone"
            name="mobilePhone"
            label={t('contact.fields.mobilePhone')}
            type="tel"
            variant="outlined"
            fullWidth
            className={classes.textField}
            InputLabelProps={{
              shrink: true,
            }}
            value={formData.mobilePhone}
            onChange={handleChange}
            error={!!errors.mobilePhone}
            helperText={errors.mobilePhone}
            disabled={isSubmitting}
            required
            autoComplete="tel"
            placeholder={t('contact.fields.mobilePhonePlaceholder')}
          />
          <TextField
            id="message"
            name="message"
            label={t('contact.fields.message')}
            variant="outlined"
            fullWidth
            className={classes.textField}
            multiline
            rows={4}
            InputLabelProps={{
              shrink: true,
            }}
            value={formData.message}
            onChange={handleChange}
            error={!!errors.message}
            helperText={`${errors.message || ''} ${formData.message.length}/500`}
            disabled={isSubmitting}
            required
            inputProps={{ maxLength: 500 }}
            placeholder={t('contact.fields.messagePlaceholder')}
          />
          <Button
            type="submit"
            variant="contained"
            color="primary"
            className={`${classes.button} ${isSubmitting ? classes.loadingButton : ''} ${submitStatus === 'success' ? classes.successButton : ''}`}
            disabled={isSubmitting}
            startIcon={
              isSubmitting ? (
                <CircularProgress size={20} color="inherit" />
              ) : submitStatus === 'success' ? (
                <CheckCircleIcon />
              ) : (
                <SendIcon />
              )
            }
          >
            <span className={classes.buttonContent}>
              {isSubmitting ? t('contact.button.sending') : 
               submitStatus === 'success' ? t('contact.button.sent') : 
               t('contact.button.send')}
            </span>
          </Button>
        </form>
        
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled">
            {snackbar.message}
          </Alert>
        </Snackbar>
      </div>
      <div className={classes.bottomShadow}></div>
    </div>
  );
};

export default Contact;
